import {combineReducers} from 'redux';
import {takeLatest} from 'redux-saga/effects';
import {simpleAsyncSaga, throttledSaga} from '../helpers/EzeeSaga';
import {EzeeAsyncAction} from '../helpers/EzeeAsyncAction';
import {EzeeSimpleAction} from '../helpers/EzeeSimpleAction';
import {Document, ListResponse, Structure} from '../api/apiTypes';
import {MainReducerState, RequestState} from '../reducers';

import {
  create as createApiCall,
  DataPayload,
  del as deleteApiCall,
  details as detailsApiCall,
  IdPayload,
  list as listApiCall,
  ListPayload,
  structure as structureApiCall,
  update as updateApiCall,
} from '../api/documents';

// State

export interface DocumentsState {
  list: RequestState<ListResponse<Document>>;
  details: RequestState<Document>;
  clean: RequestState<Document>;
  structure: RequestState<any>;
  create: RequestState<Document>;
  update: RequestState<Partial<Document>>;
  delete: RequestState<Document>;
}

const initialState: DocumentsState = {
  details: {
    data: undefined,
    loading: false,
    success: false,
  },
  clean: {
    data: undefined,
    loading: false,
    success: false,
  },
  structure: {
    data: undefined,
    loading: false,
    success: false,
  },
  list: {
    data: {
      items: [],
      totalCount: 0,
      page: 0,
      pageSize: 20,
      pageCount: 0,
    },
    loading: false,
    success: false,
  },
  create: {
    loading: false,
    success: false,
  },
  update: {
    loading: false,
    success: false,
  },
  delete: {
    loading: false,
    success: false,
  },
};

// Actions/Reducers

export const list = new EzeeAsyncAction<DocumentsState['list'],
  ListPayload,
  ListResponse<Document>>('documents/list', initialState.list, {
  trigger: (state, payload) => ({
    ...state,
    loading: true,
    error: undefined,
    success: undefined,
  }),
  success: (state, payload) => ({
      data: payload,
      loading: false,
      success: true,
      error: false,
    }
  ),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.list,
  }),
});

export const structure = new EzeeAsyncAction<DocumentsState['structure'],
  DataPayload,
  Structure>('documents/structure', initialState.structure, {
  trigger: (state) => ({
    ...state,
    loading: true,
    error: undefined,
    success: undefined,
  }),
  success: (state, payload) => ({
      data: payload,
      loading: false,
      success: true,
      error: false,
    }
  ),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.details,
  }),
});

export const details = new EzeeAsyncAction<DocumentsState['details'],
  IdPayload,
  Document>('documents/details', initialState.details, {
  trigger: (state, payload) => ({
    ...state,
    loading: true,
    error: undefined,
    success: undefined,
  }),
  success: (state, payload) => ({
      data: payload,
      loading: false,
      success: true,
      error: false,
    }
  ),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.details,
  }),
});

export const create = new EzeeAsyncAction<DocumentsState['create'],
  DataPayload,
  Document>('documents/create', initialState.create, {
  trigger: (state, payload) => ({
    ...state,
    ...payload,
    loading: true,
    success: undefined,
    error: undefined,
  }),
  success: (state, payload) => ({
    data: payload,
    loading: false,
    success: true,
    error: undefined,
  }),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.create,
  }),
});

export const update = new EzeeAsyncAction<DocumentsState['update'],
  DataPayload,
  Partial<Document>>('documents/update', initialState.update, {
  trigger: (state, payload) => ({
    ...state,
    ...payload,
    loading: true,
    success: undefined,
    error: undefined,
  }),
  success: (state, payload) => ({
    ...state,
    data: payload,
    loading: false,
    success: true,
    error: undefined,
  }),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.update,
  }),
});

export const del = new EzeeAsyncAction<DocumentsState['delete'],
  IdPayload,
  Document>('documents/delete', initialState.delete, {
  trigger: (state, payload) => ({
    ...state,
    loading: true,
    error: undefined,
    success: undefined,
  }),
  success: (state, payload) => ({
    data: payload,
    loading: false,
    success: true,
    error: false,
  }),
  failure: (state, payload) => ({
    ...state,
    loading: false,
    success: false,
    error: payload,
  }),
  reset: (state) => ({
    ...initialState.delete,
  }),
});

export const clean = new EzeeSimpleAction<DocumentsState['clean'],
  DataPayload>('clean', initialState.clean, {
  trigger: (state, payload) => ({
    data: undefined,
    loading: false,
    success: false,
  }),
});

// Reducer

export const documentsReducer = combineReducers<DocumentsState>({
  list: list.reducer,
  details: details.reducer,
  structure: structure.reducer,
  create: create.reducer,
  update: update.reducer,
  delete: del.reducer,
  clean: clean.reducer,
});

// Saga

export function* documentsSaga() {
  yield takeLatest(list.type.trigger, throttledSaga(listApiCall, list));
  yield takeLatest(details.type.trigger, simpleAsyncSaga(detailsApiCall, details));
  yield takeLatest(structure.type.trigger, simpleAsyncSaga(structureApiCall, structure));
  yield takeLatest(create.type.trigger, simpleAsyncSaga(createApiCall, create));
  yield takeLatest(update.type.trigger, simpleAsyncSaga(updateApiCall, update));
  yield takeLatest(del.type.trigger, simpleAsyncSaga(deleteApiCall, del));
}

// Store helpers

export const getDocumentsListState = (state: MainReducerState) => state.documents.list;
export const getDocumentDetailsState = (state: MainReducerState) => state.documents.details;
export const getDocumentsStructureState = (state: MainReducerState) => state.documents.structure;
export const getDocumentCreateState = (state: MainReducerState) => state.documents.create;
export const getDocumentUpdateState = (state: MainReducerState) => state.documents.update;
export const getDocumentDeleteState = (state: MainReducerState) => state.documents.delete;
